import { useState, useEffect, useCallback } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { RemoteControlStatus } from "@shared-core/data/feature-session-types";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Monitor, KeyRound, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface RemoteControlPanelProps {
  repository: string;
  branchName: string;
  enabled: boolean;
}

/**
 * Left-pane surface for a Remote Control session. Two states, chosen by the
 * container's auth status:
 *  - needs-login: a one-time brokered sign-in (open the auth URL, paste the
 *    code back). Persists across the owner's branches once done.
 *  - online: guidance to drive the session from claude.ai/code (per the "2B"
 *    discovery decision — find the session by branch name; no URL surfaced).
 */
export function RemoteControlPanel({
  repository,
  branchName,
  enabled,
}: RemoteControlPanelProps) {
  const dataAdapter = useDataAdapter();
  const [status, setStatus] = useState<RemoteControlStatus | null>(null);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [loginId, setLoginId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(
        await dataAdapter.getRemoteControlStatus(repository, branchName),
      );
    } catch {
      // transient — leave prior status
    }
  }, [dataAdapter, repository, branchName]);

  useEffect(() => {
    if (!enabled) return;
    refreshStatus();
    const id = setInterval(refreshStatus, 5000);
    return () => clearInterval(id);
  }, [enabled, refreshStatus]);

  async function handleStartLogin() {
    setBusy(true);
    try {
      const prompt = await dataAdapter.startRemoteControlLogin(
        repository,
        branchName,
      );
      setAuthUrl(prompt.auth_url);
      setLoginId(prompt.login_id);
    } catch {
      toast.error("Couldn't start sign-in. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmitCode() {
    if (!loginId || !code.trim()) return;
    setBusy(true);
    try {
      await dataAdapter.submitRemoteControlLoginCode(
        repository,
        branchName,
        loginId,
        code.trim(),
      );
      setAuthUrl(null);
      setLoginId(null);
      setCode("");
      toast.success("Signed in");
      await refreshStatus();
    } catch {
      toast.error("That code didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (status?.logged_in) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 p-8 text-center">
        <Monitor className="size-10 text-green-600" />
        <div className="space-y-1">
          <p className="font-semibold flex items-center justify-center gap-2">
            Remote Control session
            <span className="inline-flex items-center gap-1 text-xs text-green-700">
              <span className="inline-block size-1.5 rounded-full bg-green-500" />{" "}
              online
            </span>
          </p>
          <p className="text-sm text-muted-foreground max-w-sm">
            Drive this session from web, mobile, or VS Code.
          </p>
        </div>
        <ol className="text-sm text-left text-muted-foreground space-y-1.5 max-w-sm">
          <li>
            1. Open{" "}
            <a
              href="https://claude.ai/code"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              claude.ai/code <ExternalLink className="size-3" />
            </a>
          </li>
          <li>
            2. Find the session named{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              {branchName}
            </code>
          </li>
          <li>3. Start working — it stays in sync across devices</li>
        </ol>
        <p className="text-xs text-muted-foreground">
          Auto-stops after 30 min idle.
        </p>
      </div>
    );
  }

  // needs-login (active container, not signed in)
  return (
    <div className="h-full flex flex-col items-center justify-center gap-4 p-8 text-center">
      <KeyRound className="size-10 text-muted-foreground" />
      <div className="space-y-1">
        <p className="font-semibold">One-time setup: sign in to Claude</p>
        <p className="text-sm text-muted-foreground max-w-sm">
          This session runs under your own Team account. Sign in once; it stays
          signed in for future sessions on your branches.
        </p>
      </div>

      {!authUrl ? (
        <Button onClick={handleStartLogin} disabled={busy || !status?.active}>
          {busy ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
          Sign in to Claude
        </Button>
      ) : (
        <div className="space-y-3 w-full max-w-sm">
          <a
            href={authUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
          >
            Open Anthropic sign-in <ExternalLink className="size-3" />
          </a>
          <p className="text-xs text-muted-foreground">
            Sign in with your Team account, then paste the code shown:
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="paste code here"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !busy) handleSubmitCode();
              }}
            />
            <Button onClick={handleSubmitCode} disabled={busy || !code.trim()}>
              Submit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
